<template>
  <div class="text-select-input clearfix">
    <div class="form-group row">
      <label :class="options.fullWidth ? 'col-sm-12' : 'col-sm-3 col-form-label'">
        {{ options.label }}
        <span v-if="options.required" class="required">*</span>
      </label>
      <div :class="options.fullWidth ? 'col-sm-12' : 'col-sm-9'">
        <select class="form-control" ref="dropdown"
        :name="options.name" @change="validate()">
          <option v-for="(val, index) in options.existing"
            :key="index"
            :value="val.value"
            :checked="val.value === value">{{ val.label ? val.label : val[options.objectName] }}</option>
        </select>
        <span :class="validationClass" v-show="validationMessage">
          {{ validationMessage }}
        </span>
      </div>
    </div>
    <div class="loader" v-if="newAdded && !dismiss">
      <i class="fa fa-exclamation-triangle mr-2"></i> 
      Save new {{ options.label }}, {{ value }}, in database? 
      <i @click="saveNewVal()" class="fa fa-check text-success mx-3 interactive-cursor"></i> 
      <i @click="dismiss=true" class="fa fa-times text-danger interactive-cursor"></i>
    </div>
  </div>
</template>

<script>
  import Api from '@/vuex/api'
  import Choices from 'choices.js'
  import dom from '@/util/dom'
  import {
    validationClass,
    validationIcon,
    validationMessage
  } from '@/util/helpers'

  export default {
    name: 'TextSelectInput',
    props: {
      value: {
        type: String
      },
      options: {
        type: Object,
        default () {
          return {
            value: '',
            label: 'Text',
            name: 'text-select',
            existing: [],
            required: false,
            allowEdit: false
          }
        }
      }
    },
    data () {
      return {
        isDirty: false,
        isValid: false,
        currentResults: false,
        choices: false,
        saveNew: false,
        newAdded: false,
        dismiss: false,
        existingValues: [],
        uuidRegex: /^[0-9a-f]{8}-[0-9a-f]{4}-[1-4][0-9a-f]{3}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      }
    },
    updated () {
      let data = this.choices.getValue()
      let val = data ? data.value : null
      if (val !== this.value) {
        // make sure value is in the existing objects
        let found = this.choices.currentState.choices.find(el => el.value === this.value)
        if (!found) {
          this.choices.setValue([{ value: this.value, label: this.value }])
        } else {
          this.choices.setValueByChoice(this.value)
        }
        this.validate()
      }
    },
    computed: {
      invalidMessage () {
        return this.options.label + ' is required'
      },
      label () {
        return this.options.singleLabel
          ? this.options.singleLabel
          : this.options.label
      },
      validationClass,
      validationIcon,
      validationMessage
    },
    async mounted () {
      this.existingValues = this.options.existing || []
      let $vm = this
      /* eslint-disable no-new */
      this.choices = new Choices(this.$refs.dropdown, {
        placeholderValue: 'Enter a ' + this.options.label,
        noResultsText: 'Press enter to add a new value',
        removeItemButton: true,
        fuseOptions: {
          threshold: 0.0,
          maxPatternLength: 256,
          matchAllTokens: true,
          tokenize: true
        },
        searchResultLimit: 500,
        searchFields: ['label'],
        callbackOnInit: function () {
          this.containerInner.id = $vm.options.name
          if (!$vm.value) {
            let arr = this.store.getItemsFilteredByActive()
            if (arr.length > 0) {
              let value = arr.pop().value
              this.removeItemsByValue(value)
            }
          } else {
            this.setValueByChoice($vm.value)
            $vm.validate()
          }
        }
      })

      if (this.options.loadChoices) {
        switch (this.options.name) {
          case 'QuoteAssignedToUuid':
            this.existingValues = await this.getAllStaff()
            break
          default:
            break
        }
      }

      document.removeEventListener('click', this.choices._onClick)
      document.removeEventListener('keyup', this.choices._onKeyUp)
      document.removeEventListener('keydown', this.choices._onKeyDown)
      document.addEventListener('click', (e) => {
        if (e.target.className.includes('choices') && e.target.tagName !== 'BUTTON') {
          this.choices._onClick(e)
        }
      }, true)
      document.addEventListener('keydown', (e) => {
        if (document.activeElement.className.includes('choices')) {
          if (e.keyCode === 13) {
            this.choices._onKeyDown(e)
            this.isSearching = false
          } else if (e.target.className.includes('choices')) {
            this.isSearching = true
            this.choices._onKeyDown(e)
          }
        } else {
          this.isSearching = false
        }
      }, true)
      document.addEventListener('keyup', (e) => {
        if (this.isSearching && document.activeElement.className.includes('choices')) {
          this.choices._onKeyUp(e)
        }
      }, true)
      // add custom adding items behaviour
      this.$refs.dropdown.addEventListener('search', (event) => {
        this.currentResults = !this.choices.choiceList.innerHTML.includes('Press enter to add a new value')
        this.choices.input.addEventListener('keydown', this.addSearchValue)
      })
      // fix bug that causes dropdown to continually close until a key event is detected
      this.$refs.dropdown.addEventListener('showDropdown', (event) => {
        if (this.options.allowEdit) {
          let prefill = this.choices.getValue()
          this.choices.input.value = prefill ? prefill.label : ''
        } else {
          this.choices.clearInput()
        }
        this.$nextTick(function () {
          var e = new window.Event('keydown', {
            'bubbles': true
          })
          e.key = 'a'
          e.keyCode = e.key.charCodeAt(0)
          e.which = e.keyCode
          e.altKey = false
          e.ctrlKey = true
          e.shiftKey = false
          e.metaKey = false
          this.choices.input.dispatchEvent(e)
          this.choices.input.focus()
        })
      })
    },
    methods: {
      async validate (val) {
        await this.$nextTick()
        let data = false
        if (!val) {
          data = this.choices.getValue()
          val = data ? data.value : null
        }
        this.$emit('input', val)
        this.isDirty = true
        if (val) {
          this.isValid = true
        } else if (this.options.required) {
          this.isValid = false
          this.isDirty = true
        } else {
          this.isValid = true
        }
        let choicesInner = document.getElementById(this.options.name)
        if (this.isValid) {
          if (dom(choicesInner).hasClass('is-invalid')) {
            dom(choicesInner).removeClass('is-invalid')
          }
          if (!dom(choicesInner).hasClass('is-valid')) {
            dom(choicesInner).addClass('is-valid')
          }
        } else {
          if (dom(choicesInner).hasClass('is-valid')) {
            dom(choicesInner).removeClass('is-valid')
          }
          if (!dom(choicesInner).hasClass('is-invalid')) {
            dom(choicesInner).addClass('is-invalid')
          }
        }
      },
      addSearchValue (e) {
        // check if enter key is pressed, then add value
        let key = e.which || e.keyCode
        if (key === 13 && !this.currentResults) {
          this.choices.setValue([{ value: this.choices.currentValue, label: this.choices.currentValue }])
          this.validate(this.choices.currentValue)
          if (this.options.allowSave) {
            this.newAdded = true
            this.dismiss = false
          }
        }
      },
      async getAllStaff () {
        let instance = new Api()
        let staff = await instance.getAllStaff()
        let allStaff = []
        if (staff) {
          allStaff = staff.map(val => ({ value: val.uuid, label: val.fullName }))
          this.resetChoices(allStaff)
        } else {
          this.resetChoices()
        }
      },
      async getManagers (val) {
        let instance = new Api()
        let company = await instance.getCompany(val)
        if (company && company.Accounts.length) {
          company.Accounts.map(function (val, i) {
            company.Accounts[i] = { value: val.uuid, label: val.fullName }
          })
          this.resetChoices(company.Accounts)
        } else {
          this.resetChoices()
        }
      },
      resetChoices (options = this.existingValues) {
        this.newAdded = false
        let valueFound = options.find(el => el.value === this.value)
        if (valueFound || !this.isUuid(this.value)) {
          this.choices.setChoices(options, 'value', 'label', true)
        } else {
          this.$emit('input', '')
          if (this.choices) {
            this.choices.clearInput()
            if (this.choices.containerInner.children[1].children[0]) {
              this.choices.containerInner.children[1].children[0].innerHTML = ''
            }
            this.choices.setChoices(options, 'value', 'label', true)
          }
        }
      },
      async saveNewVal () {
        let instance = new Api()
        let newVal = await instance.saveNewVal({
          modelName: this.options.modelName,
          fieldName: this.options.fieldName,
          value: this.value
        })
        this.existingValues.push({
          value: newVal.uuid,
          label: newVal[this.options.fieldName]
        })
        this.resetChoices()
        this.choices.setValueByChoice(newVal.uuid)
        this.validate()
        this.dismiss = true
      },
      isUuid (uuid) {
        uuid = uuid.toLowerCase()
        return this.uuidRegex.test(uuid)
      },
      runWatcher (val, name) {
        if (this.options.watchers.includes(name)) {
          if (val) {
            switch (name) {
              case 'companyName':
                this.getManagers(val)
                break
            }
          } else {
            this.resetChoices()
            this.$emit('input', '')
          }
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/inputs/TextSelectInput.vue